import SInput from '../../../components/opinionless/SInput'
import SInputError from '../../../components/opinionless/SInputError'
import SText from '../../../components/opinionless/SText'
import SButton from '../../../components/opinionless/SButton'
import SBox from '../../../components/opinionless/SBox'
import React, { useEffect, useMemo, useState } from 'react'
import { s18n, useTranslation } from '../../../i18n'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useApplyMutation } from '../../../types/graphql'
import STheme, { SStyled, useSTheme } from '../../../styles/theme'
import { useRouter } from 'next/router'
import Select from 'react-select'
import sSelectStyle from '../../../styles/select'
import SFlex from '../../../components/opinionless/SFlex'
import SInputLabel from '../../../components/opinionless/SInputLabel'
import { PhoneNumberStyles } from '../../../styles/phoneNumber'
import SLink from 'components/opinionless/SLink'
import ConditionalRender from '../../../utils/conditionalRender'
import { isOfAge } from 'utils/date'
import STooltip from 'components/opinionless/STooltip'
import ReactDatePicker from 'react-datepicker'
import MaskedInput from 'react-text-mask'
import AddressInputGroup from '../../../components/AddressInputGroup'
import moment from 'moment'
import { ga4_useTitle } from 'utils/ga4'

interface Props {
    setCurrentStep?: (number) => void
    onSuccess: (string) => void
    fontFamily: any
}

const InputRow = SStyled(SFlex)`
    flex-direction: row;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            flex-direction: column;
        }
    `}
`

const SBoxEmail = SStyled(SBox)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-top:15px;
        }
    `}
`

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

export const languagesSelectValue = () => {
    return window.LANGUAGES.map((language) => {
        return {
            label: language.text,
            value: language.code,
        }
    })
}

export const getSelectedLanguageValue = (langCode) => {
    return {
        value: window.LANGUAGES.find((i) => i.code === langCode).code,
        label: window.LANGUAGES.find((i) => i.code === langCode).text,
    }
}

export const monthsSelectValue = (t) => {
    return [
        { label: t('january'), value: 1 },
        { label: t('february'), value: 2 },
        { label: t('march'), value: 3 },
        { label: t('april'), value: 4 },
        { label: t('may'), value: 5 },
        { label: t('june'), value: 6 },
        { label: t('july'), value: 7 },
        { label: t('august'), value: 8 },
        { label: t('september'), value: 9 },
        { label: t('october'), value: 10 },
        { label: t('november'), value: 11 },
        { label: t('december'), value: 12 },
    ]
}

export const ethnicityOptions = (t) => [
    { label: t('ethnicity_option_asian'), id: t('ethnicity_option_asian', { lng: 'en' }) },
    {
        label: t('ethnicity_option_black_african_american'),
        id: t('ethnicity_option_black_african_american', { lng: 'en' }),
    },
    { label: t('ethnicity_option_indian_alaska'), id: t('ethnicity_option_indian_alaska', { lng: 'en' }) },
    {
        label: t('ethnicity_option_hawaiian_pacific_islander'),
        id: t('ethnicity_option_hawaiian_pacific_islander', { lng: 'en' }),
    },
    { label: t('ethnicity_option_hispanic_latino'), id: t('ethnicity_option_hispanic_latino', { lng: 'en' }) },
    { label: t('ethnicity_option_white'), id: t('ethnicity_option_white', { lng: 'en' }) },
    { label: t('ethnicity_option_multiracial'), id: t('ethnicity_option_multiracial', { lng: 'en' }) },
    { label: t('ethnicity_option_other'), id: t('ethnicity_option_other', { lng: 'en' }) },
    { label: t('ethnicity_option_prefer_not_state'), id: t('ethnicity_option_prefer_not_state', { lng: 'en' }) },
]

const ApplicationStep0: React.FC<Props> = (props: Props) => {
    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [gender, setGender] = useState()

    const router = useRouter()
    const fontFamily = props.fontFamily

    const form = useForm()
    const { handleSubmit, register, errors, setValue, getValues, unregister, control } = form

    const [programApply, { loading }] = useApplyMutation()
    const theme = useSTheme()

    const { t } = useTranslation(window.LANG_NS)

    ga4_useTitle('Step 1')

    const requiredString = 'required_field'
    const isSonoma = useMemo(() => {
        return (
            window.PROGRAM.name.toLowerCase().includes('sonoma') ||
            window.PROGRAM.name.toLowerCase().includes('pathway')
        )
    }, [window])

    const isChicagoFTX = useMemo(() => {
        return (
            window.PROGRAM.name.toLowerCase().includes('chicago') && window.PROGRAM.name.toLowerCase().includes('ftx')
        )
    }, [window])

    const isLongBeach = useMemo(() => {
        return window.PROGRAM.name.toLowerCase().includes('long') && window.PROGRAM.name.toLowerCase().includes('beach')
    }, [window])

    useEffect(() => {
        if (successMessage) {
            setTimeout(() => {
                setSuccessMessage('')
            }, 3000)
        }

        if (errorMessage) {
            setTimeout(() => {
                setErrorMessage('')
            }, 3000)
        }
    }, [successMessage, errorMessage])

    useEffect(() => {
        register(
            {
                name: 'dob',
                type: 'custom',
            },
            {
                required: 'required_field' as string,
                validate: isOfAge,
            }
        )
        return () => {
            unregister('dob')
        }
    }, [])

    const languageSelected = (value) => {
        void s18n.changeLanguage(value.value)
    }

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const language = useMemo(() => {
        if (!getValues('language')) {
            if (s18n.language) {
                setValue('language', getSelectedLanguageValue(s18n.language))
                return getSelectedLanguageValue(s18n.language)
            }

            return getSelectedLanguageValue('en')
        }

        if (s18n.language) {
            setValue('language', getSelectedLanguageValue(s18n.language))
            return getSelectedLanguageValue(s18n.language)
        }
    }, [s18n.language, t])

    const lbEthnicityValues = [
        'ethnicity_option_asian',
        'ethnicity_option_black_african_american',
        'ethnicity_option_white',
        'ethnicity_option_hispanic_latino',
        'ethnicity_option_middle_eastern_north_african',
        'ethnicity_option_indian_alaska',
        'ethnicity_option_multiracial',
        'ethnicity_option_other',
        'ethnicity_option_prefer_not_state',
    ]
    const lbEthnicityOptions = lbEthnicityValues.map((ethnicityValue) => ({
        id: t(ethnicityValue, { lng: language.value }),
        label: t(ethnicityValue),
    }))

    const genderValues = ['male', 'female', 'non_binary', 'transgender', 'intersex', 'prefer_not_to_state']
    const genderOptions = genderValues.map((gender) => ({
        value: t(gender, { lng: 'en' }),
        label: t(gender),
    }))

    const onFormSubmit = (values) => {
        const variables = {
            email: values.email,
            phoneNumber: values.phoneNumber,
            dob: values.dob,
            firstName: values?.firstName,
            lastName: values?.lastName,
            language: language.value,
            programUrl: router.query['program-name'] as string,
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2?.length > 0 ? values.addressLine2 : null,
            country: 'US',
            city: values.city,
            state: values.state.value,
            postalCode: values.postalCode,
            gender: gender,
            blindDisabled: values?.blindDisabled || null,
        }

        if (isSonoma || isChicagoFTX || isLongBeach) {
            variables['ethnicity'] = values.ethnicity.id
        }

        programApply({
            variables,
        })
            .then((response) => {
                if (response.data.apply.application) {
                    const personId = atob(response.data.apply.application.id).split(':')[1]
                    const bootParams = {
                        app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
                        hide_default_launcher: false,
                    }
                    bootParams['user_id'] = `${process.env.NEXT_PUBLIC_ENVIRONMENT_NAME}-${personId}`
                    bootParams['email'] = values.email
                    bootParams['user_hash'] = response.data.apply.application.intercomKey
                    bootParams['name'] = `${values.firstName} ${values.lastName}`
                    bootParams['phone'] = values.phoneNumber
                    bootParams['created_at'] = moment().utc()

                    typeof window?.Intercom !== 'undefined' && window.Intercom('update', bootParams)

                    setTimeout(() => {
                        props.onSuccess(response.data.apply.application.id)
                        setSuccessMessage(t('application_success'))
                        props.setCurrentStep(2)
                    }, 500)
                } else {
                    if (response.data.apply.error) {
                        setErrorMessage(t('error_already_signed_up'))
                    } else {
                        setErrorMessage(t('error'))
                    }
                }
            })
            .catch(() => setErrorMessage(t('error')))
    }
    return (
        <Container>
            <FormProvider {...form}>
                <SBox
                    as="form"
                    sx={{
                        maxWidth: '700px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    onSubmit={handleSubmit(onFormSubmit)}
                >
                    <SText
                        textAlign={'left'}
                        fontWeight={'bold'}
                        fontSize={5}
                        mb={3}
                        color={STheme.colors.textColor}
                        lineHeight={1}
                        fontFamily={fontFamily.header}
                    >
                        {t('basic_information')}
                    </SText>
                    <ConditionalRender if={t('application_header_info_text').length > 0}>
                        <SText
                            fontSize={3}
                            style={{ marginBottom: '20px', color: 'darkgray' }}
                            mb={2}
                            fontFamily={fontFamily.body}
                        >
                            {t('application_header_info_text')}
                        </SText>
                    </ConditionalRender>

                    <InputRow mt={3} justifyContent={'space-between'}>
                        <SBox flex={0.47} mt={3} mb={2}>
                            <SInputLabel sx={{ fontFamily: fontFamily.body, fontSize: '14px', fontWeight: '700' }}>
                                {t('first_name_input_placeholder')}
                            </SInputLabel>
                            <SInput
                                id="firstName"
                                name="firstName"
                                placeholder={t('first_name_input_placeholder')}
                                ref={register({
                                    required: requiredString,
                                    minLength: {
                                        value: 2,
                                        message: 'error_name_at_least_two_characters',
                                    },
                                    pattern: {
                                        value: /^(?=.{2,25}$)[a-zA-ZÀ-ú-( )]+(?: [a-zA-ZÀ-ú-( )]+)?$/,
                                        message: 'error_special_characters_not_allowed',
                                    },
                                })}
                                sx={{ fontFamily: fontFamily.body }}
                            />
                            {errors.firstName && (
                                <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={'firstName'}>
                                    {t(errors.firstName?.message)}
                                </SInputError>
                            )}
                        </SBox>
                        <SBox flex={0.48} mt={3} mb={2}>
                            <SInputLabel sx={{ fontFamily: fontFamily.body, fontSize: '14px', fontWeight: '700' }}>
                                {t('last_name_input_placeholder')}
                            </SInputLabel>
                            <SInput
                                id="lastName"
                                name="lastName"
                                placeholder={t('last_name_input_placeholder')}
                                ref={register({
                                    required: requiredString,
                                    minLength: {
                                        value: 2,
                                        message: 'error_name_at_least_two_characters',
                                    },
                                    pattern: {
                                        value: /^([a-zA-ZÀ-ú-( )]+\s)*[a-zA-ZÀ-ú-( )]+$/,
                                        message: 'error_special_characters_not_allowed',
                                    },
                                })}
                                sx={{ fontFamily: fontFamily.body }}
                            />
                            {errors.lastName && (
                                <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={'lastName'}>
                                    {t(errors.lastName?.message)}
                                </SInputError>
                            )}
                        </SBox>
                    </InputRow>
                    <AddressInputGroup fontFamily={fontFamily} secondAddress={true} />
                    <InputRow mt={5} justifyContent={'space-between'}>
                        <SBox flex={0.35} mt={1}>
                            <SBox sx={{ display: 'flex !important', flexDirection: 'row !important' }}>
                                <SInputLabel
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        fontSize: '14px',
                                        fontWeight: '700',
                                        width: 'auto',
                                        marginRight: '3px',
                                    }}
                                >
                                    {t('phone_number_label')}
                                </SInputLabel>
                                <ConditionalRender if={t('phone_number_tooltip_text').length > 0}>
                                    <STooltip
                                        sx={{ fontFamily: fontFamily.body }}
                                        content={t('phone_number_tooltip_text')}
                                    ></STooltip>
                                </ConditionalRender>
                            </SBox>
                            <Controller
                                control={control}
                                name="phoneNumber"
                                rules={{
                                    required: 'required_field' as string,
                                    pattern: {
                                        value: /^\+1\d{10}$/,
                                        message: 'INVALID_PHONE_NUMBER',
                                    },
                                }}
                                height={'50px'}
                                defaultValue={null}
                                render={({ onChange, onBlur, value }) => (
                                    <SBox sx={{ position: 'relative' }} id={'phoneNumber-field-wrapper'}>
                                        <PhoneNumberStyles.PlusOne
                                            style={{ fontFamily: fontFamily.body, minHeight: '50px' }}
                                        >
                                            +1
                                        </PhoneNumberStyles.PlusOne>

                                        <PhoneNumberStyles.StyledPhoneInput
                                            id={'phoneNumber'}
                                            international={true}
                                            placeholder={t('phone_number_input_placeholder')}
                                            country={'US'}
                                            maxLength={12}
                                            value={value}
                                            onChange={onChange}
                                            style={{ fontFamily: fontFamily.body, minHeight: '50px' }}
                                        />
                                    </SBox>
                                )}
                            />
                            {errors.phoneNumber && (
                                <SInputError
                                    sx={{ fontFamily: fontFamily.body }}
                                    forElementId={'phoneNumber-field-wrapper'}
                                >
                                    {t(errors.phoneNumber?.message)}
                                </SInputError>
                            )}
                        </SBox>
                        <SBoxEmail flex={0.61}>
                            <SBox sx={{ display: 'flex !important', flexDirection: 'row !important' }}>
                                <SInputLabel
                                    alignItems={'left'}
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        fontSize: '14px',
                                        fontWeight: '700',
                                        width: 'auto',
                                        marginRight: '3px',
                                    }}
                                >
                                    {t('email_label')}
                                </SInputLabel>
                                <ConditionalRender if={t('email_tooltip_text').length > 0}>
                                    <STooltip
                                        sx={{ fontFamily: fontFamily.body }}
                                        content={t('email_tooltip_text')}
                                    ></STooltip>
                                </ConditionalRender>
                            </SBox>
                            <SInput
                                name="email"
                                id="email"
                                placeholder={t('email_input_placeholder')}
                                ref={register({
                                    required: requiredString,
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                        message: 'INVALID_EMAIL_ADDRESS',
                                    },
                                })}
                                sx={{ fontFamily: fontFamily.body }}
                            />
                            {errors.email && (
                                <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={'email'}>
                                    {t(errors.email?.message)}
                                </SInputError>
                            )}
                        </SBoxEmail>
                    </InputRow>
                    <InputRow mt={3} justifyContent={'space-between'}>
                        <SBox flex={0.48} mt={5} id="dob-wrapper">
                            <SInputLabel sx={{ fontFamily: fontFamily.body, fontSize: '14px', fontWeight: '700' }}>
                                {t('application_dob_label')}
                            </SInputLabel>
                            <Controller
                                control={control}
                                name="dob"
                                defaultValue={null}
                                render={({ onChange, value }) => {
                                    return (
                                        <ReactDatePicker
                                            onSelect={(itm) => onChange(itm)}
                                            selected={value}
                                            placeholderText={t('dob_new_placeholder')}
                                            onChange={(itm) => onChange(itm)} //
                                            customInput={
                                                <MaskedInput
                                                    mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                                    render={(ref, props) => (
                                                        <SInput name="dob" id="dob" ref={ref} {...props} />
                                                    )}
                                                />
                                            }
                                        />
                                    )
                                }}
                            />
                            {errors.dob && (
                                <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={'dob-wrapper'}>
                                    {t(errors.dob?.message)}
                                </SInputError>
                            )}
                        </SBox>

                        <ConditionalRender if={!isChicagoFTX}>
                            <SBox flex={0.48} mt={5}>
                                <SInputLabel sx={{ fontFamily: fontFamily.body, fontSize: '14px', fontWeight: '700' }}>
                                    {t('language_label')}
                                </SInputLabel>
                                <Controller
                                    name="language"
                                    control={control}
                                    defaultValue={language}
                                    render={(props: any) => {
                                        const { onChange, value } = props
                                        return (
                                            <Select
                                                name="language"
                                                id="language-field"
                                                options={languagesSelectValue()}
                                                placeholder={t('language_input_placeholder')}
                                                components={{ IndicatorSeparator: () => null }}
                                                onChange={(option: any) => {
                                                    onChange(option)
                                                    languageSelected(option)
                                                }}
                                                value={value}
                                                styles={{
                                                    ...sSelectStyle(theme),
                                                    container: (provided, state) => ({
                                                        ...sSelectStyle(theme).container(provided, state),
                                                        flex: 2,
                                                    }),
                                                    control: (provided, state) => ({
                                                        ...sSelectStyle(theme).control(provided, state),
                                                        fontFamily: fontFamily.body,
                                                    }),
                                                    option: (provided, state) => ({
                                                        ...sSelectStyle(theme).option(provided, state),
                                                        fontFamily: fontFamily.body,
                                                    }),
                                                }}
                                            />
                                        )
                                    }}
                                />
                                {errors.language && (
                                    <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={'language-field'}>
                                        {t(errors.language?.message)}
                                    </SInputError>
                                )}
                            </SBox>
                        </ConditionalRender>
                        <ConditionalRender if={isChicagoFTX}>
                            <SBox flex={0.48} mt={5}>
                                <SBox sx={{ display: 'flex !important', flexDirection: 'row !important' }}>
                                    <SInputLabel
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            color: 'black',
                                            width: 'auto',
                                            marginRight: '3px',
                                            fontFamily: fontFamily.body,
                                            fontSize: '14px',
                                            fontWeight: '700',
                                        }}
                                    >
                                        {t('gender_label')}
                                    </SInputLabel>
                                </SBox>
                                <Controller
                                    name="gender"
                                    control={control}
                                    defaultValue={null}
                                    rules={{ required: t('required_field') as string }}
                                    render={(props: any) => {
                                        const { onChange, value } = props
                                        return (
                                            <Select
                                                name="genderField"
                                                id="genderField"
                                                options={genderOptions}
                                                placeholder={t('gender_placeholder')}
                                                components={{ IndicatorSeparator: () => null }}
                                                onChange={(option: any) => {
                                                    onChange(option)
                                                    setGender(option.value)
                                                }}
                                                value={value}
                                                styles={{
                                                    ...sSelectStyle(theme),
                                                    container: (provided, state) => ({
                                                        ...sSelectStyle(theme).container(provided, state),
                                                        flex: 2,
                                                    }),
                                                    control: (provided, state) => ({
                                                        ...sSelectStyle(theme).control(provided, state),
                                                        fontFamily: fontFamily.body,
                                                    }),
                                                    option: (provided, state) => ({
                                                        ...sSelectStyle(theme).option(provided, state),
                                                        fontFamily: fontFamily.body,
                                                    }),
                                                }}
                                            />
                                        )
                                    }}
                                />
                                {errors.chicagoFtxGender && (
                                    <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={'genderField'}>
                                        {t(errors.chicagoFtxGender?.message)}
                                    </SInputError>
                                )}
                            </SBox>
                        </ConditionalRender>
                    </InputRow>
                    <InputRow mt={3} justifyContent={'space-between'}>
                        <ConditionalRender if={isSonoma || isChicagoFTX || isLongBeach}>
                            <SBox flex={1} mt={5} mb={1}>
                                <SBox sx={{ display: 'flex !important', flexDirection: 'row !important' }}>
                                    <SInputLabel
                                        sx={{
                                            fontFamily: fontFamily.body,
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            width: 'auto',
                                            marginRight: '3px',
                                        }}
                                    >
                                        {t('ethnicity_field_label')}
                                    </SInputLabel>
                                    <ConditionalRender if={t('ethnicity_tooltip_text').length > 0}>
                                        <STooltip
                                            sx={{ fontFamily: fontFamily.body }}
                                            content={t('ethnicity_tooltip_text')}
                                        ></STooltip>
                                    </ConditionalRender>
                                </SBox>
                                <Controller
                                    name="ethnicity"
                                    control={control}
                                    defaultValue={null}
                                    rules={{ required: requiredString }}
                                    render={(props: any) => {
                                        const { onChange, value } = props
                                        return (
                                            <Select
                                                name="ethnicity"
                                                id="ethnicity"
                                                mb={10}
                                                options={isLongBeach ? lbEthnicityOptions : ethnicityOptions(t)}
                                                defaultValue={null}
                                                placeholder={t('ethnicity_field_placeholder')}
                                                components={{ IndicatorSeparator: () => null }}
                                                onChange={(option: any) => {
                                                    onChange(option)
                                                }}
                                                value={value}
                                                styles={{
                                                    ...sSelectStyle(theme),
                                                    container: (provided, state) => ({
                                                        ...sSelectStyle(theme).container(provided, state),
                                                        flex: 2,
                                                    }),
                                                    control: (provided, state) => ({
                                                        ...sSelectStyle(theme).control(provided, state),
                                                        fontFamily: fontFamily.body,
                                                    }),
                                                    option: (provided, state) => ({
                                                        ...sSelectStyle(theme).option(provided, state),
                                                        fontFamily: fontFamily.body,
                                                    }),
                                                }}
                                                maxMenuHeight={500}
                                            />
                                        )
                                    }}
                                />
                                {errors.ethnicity && (
                                    <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={'ethnicity'}>
                                        {t(errors.ethnicity?.message)}
                                    </SInputError>
                                )}
                            </SBox>
                        </ConditionalRender>
                    </InputRow>
                    {successMessage && (
                        <SText color={theme.colors.secondaryColor} mt={3} mb={7} fontSize={3}>
                            {successMessage}
                        </SText>
                    )}
                    {errorMessage && (
                        <SText color={theme.colors.inputError} mt={3} mb={2} fontSize={3}>
                            {errorMessage}
                        </SText>
                    )}
                    <SButton
                        type="submit"
                        sx={['bg']}
                        title={t('continue')}
                        width={'100%'}
                        mt={6}
                        loading={loading}
                        textStyle={{ fontFamily: fontFamily.body }}
                        ga4_key={t('continue', { lang: 'en' })}
                    >
                        {t('continue')}
                    </SButton>
                    <ConditionalRender if={isSonoma}>
                        <SLink
                            href="https://first5sonomacounty.org/wp-content/uploads/2021/11/FAQS-GBI-1.pdf"
                            target="_blank"
                            style={{ color: theme.colors.tertiaryColor, marginTop: '20px' }}
                        >
                            <span style={{ fontFamily: fontFamily.body }}>{t('need_help_question')}</span>
                        </SLink>
                    </ConditionalRender>
                    <ConditionalRender if={!isSonoma && !isChicagoFTX && !isLongBeach}>
                        <SLink
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/help`}
                            style={{ color: theme.colors.tertiaryColor, marginTop: '20px' }}
                        >
                            <span style={{ fontFamily: fontFamily.body }}>{t('need_help_question')}</span>
                        </SLink>
                    </ConditionalRender>
                    <ConditionalRender if={isLongBeach}>
                        <SLink
                            href="http://longbeachpledge.org"
                            style={{
                                color: theme.colors.primaryColor,
                                fontWeight: 700,
                                marginTop: '20px',
                                fontSize: '14px',
                                fontFamily: fontFamily.body,
                                lineHeight: '18.2px',
                            }}
                            target="_blank"
                        >
                            <span>{t('need_help_question')}</span>
                        </SLink>
                    </ConditionalRender>
                </SBox>
            </FormProvider>
        </Container>
    )
}

export default ApplicationStep0
